// Cook County tax bills — the arithmetic, from a TRANSCRIPTION (api/extract.js docType 'cook_bill'). The model reads
// the Tax Calculator box and the Taxing District Breakdown rows as printed; everything below is derived here, so a
// Cook bill seeds the review form from the bill's own figures:
//
//   Taxable Value    = the printed EAV (Equalized Assessed Value) — what the local rate is applied to.
//   Equalizer        = the printed State Equalizer (3.0163), saved as tax_bills.equalization_factor (Philip 2026-10-30):
//                      reports show the printed local rate beside it rather than a derived combined rate.
//   Total Tax Rate   = the printed Local Tax Rate (6.995000%). NEVER the sum of the breakdown's rates: the breakdown
//                      prints four "… Taxes Total" subtotal rows and a "(Do not pay these totals)" row, and summing
//                      them with the districts doubled the rate (7.035 → 14.07 on 19-10-320-052-8006 / 2022).
//   Market Value     = the printed "Property Value" (2024+ bills print it), else Assessed Value ÷ the printed
//                      Assessment Level, else ÷ 25% for class 5 (commercial), else blank — never a guess.
//   Lines            = the district rows, minus every row whose name ends in "Total"/"totals)" (subtotals).
//   Non Ad Valorem   = 0. Every Cook district line is a rate-based levy, the TIF line included.
//   Ad Valorem       = the printed total tax when the lines add up to it, else the line sum (and the screen's
//                      reconcile banner says so).
//
// ⛔ TIF. Inside a Tax Increment Financing district every regular district taxes only the FROZEN BASE EAV, and the
// TIF line collects (EAV − base) × the full local rate. The bill prints the TIF line with rate 0.000, so read as
// printed it neither reconciles nor counts as ad valorem (it landed in Non Ad Valorem). The base is not printed; it
// is recovered from the regular lines — each one is base × its rate, so amount ÷ rate gives the base on every line
// (to rounding), and the median of those is taken. Then each regular line's taxable value = base, and the TIF line's
// = EAV − base at the full local rate, so every line reconciles. 19-10-320-052-8006 / 2023: base 419,472, TIF
// increment 1,383,159 × 6.995% = 96,751.96.

const n = (v) => {
  if (v == null) return null;
  const s = String(v).replace(/[$,%\s]/g, '').replace(/^X/i, '');
  if (s === '' || !/^-?\d*\.?\d+$/.test(s)) return null;
  const x = Number(s);
  return Number.isFinite(x) ? x : null;
};
const r2 = (x) => Math.round(x * 100) / 100;
// The rate as printed, minus the % — kept as a string so its printed precision sets the line's reconcile tolerance.
const rateStr = (v) => (v == null ? null : String(v).replace(/[%\s]/g, '').replace(/^X/i, '') || null);

// A subtotal row: "School Taxes Total", "COOK COUNTY TAXES TOTAL", "(Do not pay these totals)".
export const isCookTotalRow = (name) => /\btotals?\s*\)?\s*$/i.test(String(name || '').trim());
export const isTifLine = (name) => /\bTIF\b/i.test(String(name || ''));

// Is this extraction a Cook County bill? The collector name says so, or an IL bill carries a Cook PIN (14 digits as
// 2-2-3-3-4).
export function isCookBill(ext) {
  if (!ext) return false;
  if (/\bcook\b/i.test(String(ext.collector || ''))) return true;
  const pin = /^\d{2}-\d{2}-\d{3}-\d{3}-\d{4}$/;
  return String(ext.state || '').toUpperCase() === 'IL'
    && (pin.test(String(ext.parcel_number || '').trim()) || pin.test(String(ext.account_number || '').trim()));
}

const median = (xs) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

// → { ok:false, reason } or { ok:true, lines, taxable_value, market_value, total_tax_rate, total_ad_valorem,
//     total_non_ad_valorem, total_tax, eav, equalizer, base, tif_increment, notes[] }
export function deriveCookBill(t) {
  if (!t) return { ok: false, reason: 'no Cook transcription' };
  const eav = n(t.eav);
  const rate = n(t.local_tax_rate);
  const total = n(t.total_tax_after_exemptions) ?? n(t.total_tax_before_exemptions);
  const rows = Array.isArray(t.districts) ? t.districts : [];
  const totalsRow = rows.find(d => /do not pay/i.test(String(d?.name || '')));
  const districts = rows.filter(d => d && String(d.name || '').trim() && n(d.tax) != null && !isCookTotalRow(d.name));
  if (eav == null || eav <= 0) return { ok: false, reason: 'the EAV was not read' };
  if (rate == null || rate <= 0) return { ok: false, reason: 'the Local Tax Rate was not read' };
  if (!districts.length) return { ok: false, reason: 'no taxing district lines were read' };
  const notes = [];
  // The TIF line's rate at the breakdown's own precision (the "(Do not pay these totals)" row prints it), so the
  // TIF line reconciles to the same tolerance as the district lines beside it.
  const fullRate = (totalsRow && n(totalsRow.rate) > 0) ? rateStr(totalsRow.rate) : String(rate);

  const tif = districts.filter(d => isTifLine(d.name) && (n(d.tax) || 0) > 0);
  let base = eav, increment = null;
  if (tif.length) {
    const estimates = districts
      .filter(d => !isTifLine(d.name) && (n(d.rate) || 0) > 0 && (n(d.tax) || 0) > 0)
      .map(d => n(d.tax) / (n(d.rate) / 100));
    if (!estimates.length) return { ok: false, reason: 'a TIF bill with no regular district line to recover the base from' };
    base = Math.round(median(estimates));
    increment = eav - base;
    if (increment <= 0) return { ok: false, reason: `the recovered TIF base (${base}) is not below the EAV (${eav})` };
    if (tif.length > 1) notes.push(`${tif.length} TIF lines — each was given the full increment; check them against the bill.`);
  }

  const lines = districts.map(d => {
    const isTif = increment != null && isTifLine(d.name) && (n(d.tax) || 0) > 0;
    return {
      name: String(d.name).trim(),
      taxable_value: String(isTif ? increment : base),
      tax_rate: isTif ? fullRate : rateStr(d.rate),
      amount: String(n(d.tax)),
    };
  });

  const lineSum = r2(lines.reduce((a, l) => a + (n(l.amount) || 0), 0));
  const tol = Math.max(2, lines.length);   // ~$1/line of rounding, as the screen's own reconcile allows
  const footsToTotal = total != null && Math.abs(lineSum - total) <= tol;
  if (total != null && !footsToTotal) notes.push(`The district lines add to ${lineSum.toFixed(2)}, not the printed total ${total.toFixed(2)}.`);

  // Market Value: printed Property Value → Assessed ÷ printed Assessment Level → Assessed ÷ 25% for class 5 → blank.
  const assessed = n(t.assessed_value);
  const level = n(t.assessment_level);
  const cls = String(t.classification || '').trim();
  let market = n(t.property_value);
  if (market == null && assessed != null && level) market = Math.round(assessed / (level / 100));
  if (market == null && assessed != null && /^5/.test(cls)) market = Math.round(assessed / 0.25);
  if (market == null) notes.push(`Market Value left blank — the bill prints no Property Value or Assessment Level and class "${cls || 'unknown'}" is not class 5.`);

  return {
    ok: true,
    lines,
    eav, base: increment != null ? base : null, tif_increment: increment,
    equalizer: n(t.state_equalizer),
    taxable_value: eav,
    market_value: market,
    total_tax_rate: rate.toFixed(6),
    total_tax: total,
    total_ad_valorem: footsToTotal ? total : lineSum,
    total_non_ad_valorem: 0,
    notes,
  };
}
